import {FormattedMessage, useIntl} from 'react-intl';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate, useParams} from 'react-router-dom';

import {
  addReferrals,
  cmServiceAddToFavorites,
  cmServiceAsyncActions, cmServiceRemoveFromFavorites,
  getCmService,
  getTranslations, referralsAsyncActions, removeReferrals
} from 'redux/pageAsyncActions';
import {IStore} from 'redux/interface';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import NotFoundInfo from 'components/not-found-info';
import Loader from 'components/loader';

import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import NotFoundImg from 'assets/image/common/notFound.png';
import {ReactComponent as FullHeart} from '../../assets/image/library/full-heart.svg';
import {ReactComponent as EmptyHeart} from '../../assets/image/library/empty-heart.svg';
import {useLoadFile} from '../../utils/hooks/useLoadFile';
import {ICmLocation} from '../../redux/cm-service/interfaces';

import './styles.scss';

export const CmResourceCard: React.FunctionComponent = () => {
  const {
    service
  } = useSelector((state: IStore) => state.cm_service);
  const {
    loading,
    errors
  } = useSelector((state: IStore) => state.status);
  const {
    userProfile, settings
  } = useSelector((state: IStore) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const {srvId} = useParams();
  const [isAddedToMaterials, setIsAddedToMaterials] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [locations, setLocations] = useState<ICmLocation[]>();
  const [languages, setLanguages] = useState<string>();
  const [counties, setCounties] = useState<string>();
  const [ages, setAges] = useState<string>();
  const [typeTreatments, setTypeTreatments] = useState<string>();
  const [referralsRole, setReferralsRole] = useState<string | null>(null);
  const [showReferralsButton, setShowReferralsButton] = useState<boolean>(false);

  const {
    getFileUrl
  } = useLoadFile();

  useGetTranslations({
    translationKey: TranslationKeys.CmResource
  });

  useEffect(() => {
    if (srvId) {
      dispatch(cmServiceAsyncActions[getCmService](Number(srvId)));
    }
  }, [srvId]);

  const distinctById = <T extends { id: string | number }>(
    items: ReadonlyArray<T>
  ): T[] => items.reduce<{ seen: Record<string, true>; out: T[] }>(
    (acc, item) => {
      const key = String(item.id);

      if (!acc.seen[key]) {
        acc.seen[key] = true;
        acc.out.push(item);
      }

      return acc;
    },
    {seen: Object.create(null), out: [] as T[]}
  ).out;

  useEffect(() => {
    if (service) {
      setIsAddedToMaterials(service.material);
      setIsFavorite(service.favorite);
      if (!service.srvImageUrl && service.srvImageId) {
        getFileUrl(service.srvImageId, (url) => {
          if (url) {
            setImageUrl(url);
          }
        });
      } else {
        setImageUrl(service.srvImageUrl);
      }

      if (service.locations && service.locations.length) {
        setLocations(distinctById(service.locations));
        setCounties(service.locations.filter(l => l.county).map(l => l.county).join(', '));
      }
      if (service.languages) {
        setLanguages(service.languages.split(/[, ]+/).join(', '));
      }
      if (service.ages) {
        setAges(service.ages.split(/[, ]+/).join(', '));
      }
      if (service.typeTreatments) {
        setTypeTreatments(service.typeTreatments.split(/[, ]+/).join(', '));
      }
    }
  }, [service]);

  useEffect(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Referrals.toLowerCase());

    if (page) {
      setShowReferralsButton(true);
      if (page.role) {
        setReferralsRole(page.role);
      }
    }
  }, [settings]);

  function addToReferrals() {
    setIsAddedToMaterials(!isAddedToMaterials);
    if (service) {
      if (isAddedToMaterials) {
        dispatch(referralsAsyncActions[removeReferrals]([service.srvId], false));
      } else {
        dispatch(referralsAsyncActions[addReferrals](service.srvId));
      }
    }
  }

  function changeFavorite() {
    setIsFavorite(!isFavorite);
    if (service) {
      if (!isFavorite) {
        dispatch(cmServiceAsyncActions[cmServiceAddToFavorites](service.srvId));
      } else {
        dispatch(cmServiceAsyncActions[cmServiceRemoveFromFavorites](service.srvId));
      }
    }
  }

  function handleOpenServiceOrg() {
    navigate(`/cmresource/org/${service?.orgId}`);
  }

  function renderBreadcrumbs() {
    return <div className="breadcrumbs">
      <Link className="breadcrumbs__previous" to={'/cmresource'}>
        <FormattedMessage
          id={'gritx.cmresource.breadcrumb.previous'}
          defaultMessage='Resources'
        />
      </Link>
    </div>;
  }

  const renderAddToReferralsButton = () => (showReferralsButton && !!referralsRole && userProfile?.roles.includes(referralsRole)
    && <GritxButton
      title={isAddedToMaterials
        ? intl.formatMessage({
          id: 'gritx.cmresource.card.saved',
          defaultMessage: 'Saved'
        })
        : intl.formatMessage({
          id: 'gritx.cmresource.card.addToReferrals',
          defaultMessage: 'Add to Referrals'
        })}
      variant={ButtonVariant.Primary}
      onClick={addToReferrals}
    />);

  const renderActionsSection = () => {
    return <>
      {renderAddToReferralsButton()}
      <button className="cm-resource-card__button-icon" onClick={changeFavorite}>
        <div className="cm-resource-card__icon">
          {
            isFavorite
              ? <FullHeart className="cm-resource-card__icon-img"/>
              : <EmptyHeart className="cm-resource-card__icon-img"/>
          }
        </div>
      </button>
    </>;
  };

  function renderContent() {
    if (loading.includes(getCmService)) {
      return <div className="cm-resource-card__view">
        <div className="cm-resource-card__data"><Loader nested/></div>
      </div>;
    }

    if (errors[getCmService]?.error.message.includes('500') || errors[getCmService]?.error.message.includes('400')) {
      return <div className="cm-resource-card__view">
        <div className="cm-resource-card__data">
          <NotFoundInfo
            title={intl.formatMessage({
              id: 'gritx.cmresource.notFoundCard',
              defaultMessage: 'Sorry, we can\'t find the resource you are looking for.'
            })}
            image={NotFoundImg}/>
        </div>
      </div>;
    }

    if (service) {
      return <div className="cm-resource-card__view">
        <div className="cm-resource-card__data">
          <div className="cm-resource-card__title">
            <h2 className="cm-resource-card__heading">{service.srvName}</h2>
          </div>
          <div className="cm-resource-card__props">
            <div className="cm-resource-card__actions">
              {renderActionsSection()}
            </div>
          </div>
          <div className="cm-resource-card__body">
            <p className="cm-resource-card__description">
              {service?.orgId && <button className="cm-resource-card__org_button"
                onClick={handleOpenServiceOrg}>
                <FormattedMessage
                  id={'gritx.cmresource.card.orgBy'}
                  defaultMessage='By'
                />{': '}
                {service.orgName}
              </button>}
            </p>
            {imageUrl && <div className="cm-resource-card__image-wrapper">
              {
                <img src={imageUrl} alt="" className="cm-resource-card__image"/>
              }
            </div>}
            <div className="cm-resource-card__panel-content">
              <div className="cm-resource-card__left-column">
                <section className="cm-resource-card__section">
                  <h2><FormattedMessage
                    id={'gritx.cmresource.card.mainServices'}
                    defaultMessage='Main services'
                  />:</h2>
                  <p>{typeTreatments}</p>
                </section>
                {locations && locations.length && <section className="cm-resource-card__section">
                  <h2><FormattedMessage
                    id={'gritx.cmresource.card.contacts'}
                    defaultMessage='Contacts'
                  />:</h2>
                  {locations.map(location => {
                    return <div key={location.id} className="cm-resource-card__section">
                      {location.city && <p><strong><FormattedMessage
                        id={'gritx.cmresource.card.city'}
                        defaultMessage='City'
                      />: </strong>{location.city}</p>}
                      {location.address && <p><strong><FormattedMessage
                        id={'gritx.cmresource.card.address'}
                        defaultMessage='Address'
                      />: </strong>{location.address}</p>}
                      {location.phone && <p><strong><FormattedMessage
                        id={'gritx.cmresource.card.phone'}
                        defaultMessage='Phone'
                      />: </strong>{location.phone}</p>}
                      {location.website && <p><strong>Web: </strong>
                        <a href={location.website} target="_blank" rel="noreferrer">{location.website}</a>
                      </p>}
                    </div>;
                  })}
                </section>}
                <section className="cm-resource-card__section">
                  <h2><FormattedMessage
                    id={'gritx.cmresource.card.process'}
                    defaultMessage='Process'
                  />:</h2>
                  <p>{service.process}</p>
                </section>
                <section className="cm-resource-card__section">
                  <h2><FormattedMessage
                    id={'gritx.cmresource.card.notes'}
                    defaultMessage='Notes'
                  />: </h2>
                  <p>{service.treatmentDescription}</p>
                </section>
              </div>
              <div className="cm-resource-card__right-column">
                <section className="cm-resource-card__section">
                  <h3><FormattedMessage
                    id={'gritx.cmresource.card.counties'}
                    defaultMessage='Counties Served'
                  />:</h3>
                  <p>{counties}</p>
                </section>
                <section className="cm-resource-card__section">
                  <h3><FormattedMessage
                    id={'gritx.cmresource.card.serving'}
                    defaultMessage='Serving'
                  />:</h3>
                  <p>{ages}</p>
                </section>
                <section className="cm-resource-card__section">
                  <h3><FormattedMessage
                    id={'gritx.cmresource.card.languages'}
                    defaultMessage='Languages'
                  />:</h3>
                  <p>{languages}</p>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>;
    }

    return null;
  }

  return <div className="cm-resource-card">
    {
      !loading.includes(getTranslations) && <>
        {renderBreadcrumbs()}
        <div className="cm-resource-card__wrapper">
          {renderContent()}
        </div>
      </>
    }
  </div>;
};
